/**
 * @fileoverview Japanese translations.
 * @summary Japanese translation strings for KaraoNow frontend.
 *
 * @package KaraoNow
 * @module apps/front/src/utils/i18n/locales/ja
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 * @license EULA
 */
export const ja = {
  common: {
    loading: '読み込み中...',
    processing: '処理中',
    error: 'エラー',
    success: '成功',
    cancel: 'キャンセル',
    confirm: '確認',
    save: '保存',
    delete: '削除',
    edit: '編集',
    create: '作成',
    back: '戻る',
    next: '次へ',
    previous: '前へ',
    search: '検索',
    filter: 'フィルター',
    close: '閉じる',
    backHome: 'ホームに戻る',
    creating: '作成中...',
  },
  auth: {
    login: 'ログイン',
    register: '登録',
    logout: 'ログアウト',
    email: 'メールアドレス',
    password: 'パスワード',
    confirmPassword: 'パスワード確認',
    forgotPassword: 'パスワードを忘れた場合',
    rememberMe: 'ログイン状態を保持',
    loginButton: 'ログイン',
    registerButton: '登録',
    alreadyHaveAccount: '既にアカウントをお持ちですか？',
    dontHaveAccount: 'アカウントをお持ちでない場合',
    role: '役割',
    organizer: '主催者',
    singer: '歌手',
    firstName: '名',
    lastName: '姓',
    pseudo: 'ペンネーム',
    organization: '組織',
    sendVerificationEmail: '確認メールを送信',
    emailVerificationSent: '確認メールを送信しました',
    emailVerificationInstructions: '{{email}}に確認メールを送信しました。メール内のリンクをクリックして登録を完了してください。',
    backToLogin: 'ログインに戻る',
    optional: '任意',
    registerOrganizerTitle: '主催者登録',
    registerSingerTitle: '歌手登録',
    registerOrganizerDescription: 'カラオケイベントを主催するためのアカウントを作成',
    registerSingerDescription: 'カラオケイベントに参加するためのアカウントを作成',
    setPassword: 'パスワードを作成',
    setPasswordTitle: '登録を完了',
    setPasswordDescription: '登録を完了するためにパスワードを作成してください',
    createPassword: 'パスワードを作成',
    passwordRequirements: '最低12文字、大文字、小文字、数字、特殊文字を含む',
    forgotPasswordDescription: 'メールアドレスを入力すると、パスワードリセット用のリンクをお送りします。',
    sendResetLink: 'リセットリンクを送信',
    resetPasswordEmailSent: 'パスワードリセットメールを送信しました',
    resetPasswordInstructions: '{{email}}にパスワードリセットメールを送信しました。メール内のリンクをクリックしてパスワードをリセットしてください。',
    resetPassword: 'パスワードをリセット',
    resetPasswordDescription: 'アカウントの新しいパスワードを作成',
    resetPasswordButton: 'パスワードをリセット',
    passwordResetSuccess: 'パスワードがリセットされました',
    passwordResetSuccessDescription: 'パスワードが正常にリセットされました。ログインページにリダイレクトされます。',
    resetLinkExpired: 'リセットリンクが期限切れです',
    resetLinkExpiredDescription: 'このリセットリンクは期限切れです（有効期間は1時間）。新しいリンクをリクエストしてください。',
    requestNewResetLink: '新しいリンクをリクエスト',
    rememberPassword: 'パスワードを覚えていますか？',
  },
  // Organizer menu
  organizerMenu: {
    menu: 'オーガナイザーメニュー',
    dashboard: 'ダッシュボード',
    venues: '会場',
    settings: '設定',
    dataPrivacy: '個人データ',
    subscriptions: '価格',
    user: 'ユーザー',
  },
  errors: {
    genericMessage: 'エラーが発生しました',
    required: 'この項目は必須です',
    invalidEmail: '無効なメールアドレス',
    passwordTooShort: 'パスワードは12文字以上である必要があります',
    passwordRequirements: 'パスワードには大文字、小文字、数字、特殊文字がそれぞれ1つ以上含まれている必要があります',
    passwordsDoNotMatch: 'パスワードが一致しません',
    loginFailed: 'ログインに失敗しました',
    registerFailed: '登録に失敗しました',
    resetPasswordFailed: 'パスワードリセットに失敗しました',
    invalidToken: '無効または期限切れのトークン',
    networkError: 'ネットワークエラー',
    unauthorized: '認証が必要です',
    forbidden: 'アクセスが拒否されました',
    notFound: {
      title: 'ページが見つかりません',
      description: 'お探しのページは存在しないか、移動されました。',
      explore: 'アプリを探索',
    },
    server: {
      title: 'サーバーエラー',
      description: '予期しないエラーが発生しました。後でもう一度お試しください。',
    },
    generic: {
      title: '問題が発生しました',
      description: '予期しないエラーが発生しました。もう一度お試しください。',
    },
    serverError: 'サーバーエラー',
    // Error codes from backend
    VALIDATION_ERROR: '検証エラー',
    CREATE_EVENT_ERROR: 'イベントの作成エラー',
    UPDATE_EVENT_ERROR: 'イベントの更新エラー',
    EVENT_NOT_FOUND: 'イベントが見つかりません',
    VENUE_NOT_FOUND: '会場が見つかりません',
    QUOTA_EXCEEDED_CREATE: 'イベント作成の月間クォータに達しました',
    QUOTA_EXCEEDED_DATE_CHANGE: '{{planName}}プランの月間クォータに達しました：月最大{{limit}}イベント。クォータは請求期間の終了時にリセットされます。',
    SUBSCRIPTION_EVENT_CANNOT_CONVERT: 'サブスクリプションプランに含まれるイベントは単一イベントに変換できません',
    EVENT_DATE_NOT_REACHED: '予定された日付より前にイベントをアクティブにすることはできません',
    EVENT_DATE_PAST: 'イベントの日付は過去に設定できません',
    EVENT_DATE_AFTER_EXPIRATION: 'イベントの日付は、ユニークイベントの有効期限を超えることはできません',
    // Validation errors by field and Zod code
    validation: {
      name: {
        too_small: 'イベント名は必須です',
        required: 'イベント名は必須です',
        invalid_type: 'イベント名が無効です',
      },
      date: {
        required: '日付は必須です',
        invalid_type: '日付が無効です',
        invalid_date: '日付が無効です',
        invalid_string: '日付の形式が無効です',
      },
      description: {
        invalid_type: '説明が無効です',
      },
      venueId: {
        invalid_type: '会場が無効です',
      },
    },
  },
  // Landing page
  landing: {
    languageLabel: '言語を選んでください',
    heroTitle: 'カラオケの夜を、もっとシンプルに。',
    heroSubtitle: '2分でイベント作成。ゲストがQRコードをスキャンして曲を追加 — あなたが全体の流れをコントロール。',
    ctaPrimary: '初めてのカラオケを作る — 無料',
    ctaOrganize: '主催したい',
    ctaOrganizeDesc: 'イベントを作成し、プレイリストを管理し、順番を把握。',
    ctaSing: '歌いたい',
    ctaSingDesc: 'QRコードでイベントに参加し、曲を追加。',
    alreadyAccount: 'すでにアカウントをお持ちですか？',
    login: 'ログイン',
    whyTitle: 'なぜKaraoNow？',
    benefit1Title: 'QRコード1つ、紙はゼロ',
    benefit1Desc: 'ゲストがスマホでスキャンして曲を選択。紙のリスト紛失とはサヨナラ。',
    benefit2Title: 'ライブプレイリスト',
    benefit2Desc: '順番が誰の目にも見える。曲の流れをストレスなく進められます。',
    benefit3Title: 'まずは無料で',
    benefit3Desc: '無料イベントは最大4回。いつでも無制限プランへ。',
    twoWaysTitle: 'KaraoNowの2つの使い方',
    learnMore: '詳しく見る',
  },
  home: {
    title: 'カラオケイベントのプレイリスト管理',
    wantToSing: '歌いたい',
    wantToSingDescription: 'イベントに参加して曲を追加',
    wantToOrganize: '主催したい',
    wantToOrganizeDescription: 'カラオケイベントを作成・管理',
    alreadyHaveAccount: '既にアカウントをお持ちですか？',
  },
  dashboard: {
    title: '主催者ダッシュボード',
    welcome: 'ようこそ',
    events: 'イベント',
    createEvent: 'イベントを作成',
    noEvents: 'イベントがありません',
    noEventsDescription: '最初のカラオケイベントを作成することから始めましょう',
    filterAll: 'すべて',
    filterDraft: '下書き',
    filterActive: 'アクティブ',
    filterCompleted: '完了',
    filterCancelled: 'キャンセル',
    loadingEvents: 'イベントを読み込み中...',
    errorLoadingEvents: 'イベントの読み込みエラー',
    noEventsMatchingFilter: 'このフィルターに一致するイベントがありません',
    tryDifferentFilter: '別のフィルターを選択してみてください',
  },
  events: {
    name: 'イベント',
    events: 'イベント',
    song: '曲',
    songs: '曲',
    description: '説明',
    date: '日付',
    time: '時間',
    venue: '会場',
    selectVenue: '会場を選択',
    namePlaceholder: '私のカラオケナイト',
    descriptionPlaceholder: 'オプション説明...',
    createTitle: 'イベントを作成',
    createSubtitle: '新しいカラオケイベントを作成',
    createButton: 'イベントを作成',
    createEvent: 'イベントを作成',
    createSuccess: 'イベントが正常に作成されました',
    createError: 'イベントの作成エラー',
    noVenues: '利用可能な会場がありません。最初に会場を作成してください。',
    createVenue: '会場を作成',
    status: '状態',
    statusDraft: '下書き',
    statusActive: 'アクティブ',
    statusCompleted: '完了',
    statusCancelled: 'キャンセル',
    statusHidden: '非表示',
    createdAt: '作成日時',
    updatedAt: '更新日時',
    view: '表示',
    open: '開く',
    edit: '編集',
    delete: '削除',
    qrCode: 'QRコード',
    deleteConfirmTitle: 'イベントを削除',
    deleteConfirm: 'イベント "{{name}}" を削除してもよろしいですか？',
    deleteSuccess: 'イベントが正常に削除されました',
    deleteError: 'イベントの削除エラー',
    managementTitle: 'イベント管理',
    editEvent: 'イベントを編集',
    updateSuccess: 'イベントが正常に更新されました',
    updateError: 'イベントの更新エラー',
    showQRCode: 'QRコードを表示',
    hideQRCode: 'QRコードを非表示',
    qrCodeTitle: 'イベントQRコード',
    qrCodeDescription: '参加者とこのコードを共有して、イベントに参加できるようにします',
    copyQRCode: 'リンクをコピー',
    qrCodeCopied: 'リンクがクリップボードにコピーされました',
    shareQRCode: '共有',
    queue: 'キュー',
    singers: '歌手',
    noQueue: 'キューに曲がありません',
    noSingers: '登録された歌手がいません',
    guest: 'ゲスト',
    singer: '歌手',
    songsCount_one: '{{count}}曲',
    songsCount_other: '{{count}}曲',
    deleteQueueItem: '曲を削除',
    deleteQueueItemConfirm: 'この曲をキューから削除してもよろしいですか？',
    deleteSinger: '歌手を削除',
    deleteSingerConfirm: 'この歌手を削除してもよろしいですか？キュー内のすべての曲も削除されます。',
    singerDeleteError: '歌手の削除中にエラーが発生しました',
    moveUp: '上に移動',
    moveDown: '下に移動',
    dragToReorder: 'ドラッグして並べ替え',
    setCurrent: '現在の曲に設定',
    markCompleted: '完了としてマーク',
    cancel: 'キャンセル',
    queueUpdateError: 'キューの更新中にエラーが発生しました',
    queueDeleteError: '曲の削除中にエラーが発生しました',
    queueStatusPending: '待機中',
    queueStatusCurrent: '再生中',
    queueStatusCompleted: '完了',
    queueStatusCancelled: 'キャンセル',
    qrCodeUrl: 'イベントURL',
    qrCodeShareText: 'このカラオケイベントに参加しましょう：{{eventName}}！',
    qrCodePrintTitle: 'QRコード - KaraoNow',
    qrCodePrintInstructions: 'このQRコードをスキャンしてイベントにアクセスし、キューに曲を追加してください。',
    qrCodePrint: '印刷',
    qrCodeShare: '共有',
    qrCodeDownload: 'ダウンロード',
    startEvent: 'イベントを開始',
    start: '開始',
    completeEvent: 'イベントを完了',
    complete: '完了',
    cancelEvent: 'イベントをキャンセル',
    cancelConfirmTitle: 'キャンセルの確認',
    cancelConfirmMessage: 'このイベントをキャンセルしてもよろしいですか？この操作は取り消せません。',
    cancelConfirmOneShotTitle: 'キャンセルの確認 - 一回限りのイベント',
    cancelConfirmOneShotMessage: '⚠️ 注意：この一回限りのイベントは購入済みです。キャンセルは最終的なもので、イベントが使用されていない場合でも返金は行われません。このイベントをキャンセルしてもよろしいですか？',
    cancelConfirmButton: 'はい、キャンセルします',
    cancelCancelButton: 'いいえ、イベントを保持します',
    deleteEvent: 'イベントを削除',
    deleteEventConfirm: 'このイベントを削除してもよろしいですか？この操作は取り消せません。',
    hide: '非表示にする',
    hideConfirmTitle: 'イベントを非表示',
    hideConfirm: 'イベント "{{name}}" を非表示にしますか？メインリストには表示されなくなりますが、月間クォータには引き続きカウントされます。',
    hideSuccess: 'イベントが正常に非表示になりました',
    hideError: 'イベントの非表示中にエラーが発生しました',
    hidden: '非表示',
    showHidden: '非表示',
    hiddenEventTitle: '非表示のイベント',
    hiddenEventMessage: 'このイベントは非表示になっており、アクセスできません。月間クォータには引き続きカウントされます。',
    hiddenEventTooltip: 'このイベントは非表示になっており、表示できません',
    statusUpdated: 'イベントのステータスが更新されました',
    backToEvent: 'イベントに戻る',
    // Quota exceeded for date change
    quotaExceededDateChange: 'この日付の月間クォータに達しました',
    quotaExceededDateChangeTitle: '月間クォータに達しました',
    quotaExceededDateChangeExplanation: 'イベント「{{eventName}}」の日付を変更することはできません。月間イベントクォータを超えてしまいます。',
    quotaExceededDateChangeOptions: '元の日付を維持するか、このイベントを「単一イベント」に変換して月間クォータから除外することができます。',
    quotaExceededDetails: 'この期間に{{current}} / {{limit}}イベントがあります。',
    upgradeToOneShotOption: 'オプション：単一イベントにアップグレード',
    upgradeToOneShotDescription: 'このイベントを「単一イベント」に変換すると、月間クォータにカウントされなくなり、日付を自由に変更できるようになります。',
    upgradeToOneShotNow: '🚀 単一イベントにアップグレード',
    keepOriginalDate: '元の日付を維持',
    multipleEventsPremiumSuggestion: '複数のイベント？プレミアムプランの方が費用対効果が高いかもしれません。',
    // Quota exceeded for event creation
    quotaExceededCreateTitle: '月間クォータに達しました',
    quotaExceededCreateMessage: '{{planName}}の制限に達しました。この期間にこれ以上イベントを作成することはできません。',
    quotaExceededCreateExplanation: '月間イベントクォータに達しました。請求期間の終了を待つか、上位プランにアップグレードすることができます。',
    quotaExceededCreateOptions: '月間クォータにカウントされない単一イベントを作成することもできます。',
    // Quota exceeded for FREE plan (no monthly/billing period references)
    quotaExceededCreateTitleFree: 'クォータに達しました',
    quotaExceededCreateMessageFree: '{{planName}}の制限に達しました。これ以上イベントを作成することはできません。',
    quotaExceededCreateExplanationFree: '無料プランのイベント制限に達しました。上位プランにアップグレードして、より多くのイベントを作成できます。',
    quotaExceededCreateOptionsFree: 'クォータにカウントされない単一イベントを作成することもできます。',
    quotaExceededDetailsFree: '{{current}} / {{limit}}イベントがあります。',
    createOneShotDescriptionFree: 'クォータにカウントされない単一イベントを作成します。',
    upgradePlanDescriptionFree: '上位プランにアップグレードして、イベントクォータを増やします。',
    createOneShotOption: 'オプション：単一イベントを作成',
    createOneShotDescription: '月間クォータにカウントされない単一イベントを作成します。',
    createOneShotNow: '🚀 単一イベントを作成',
    upgradePlanOption: 'オプション：上位プランにアップグレード',
    upgradePlanDescription: '上位プランにアップグレードして、月間イベントクォータを増やします。',
    upgradePlanNow: '上位プランにアップグレード',
    quotaResetInfo: 'クォータは請求期間の終了時にリセットされます。',
    // Generic quota exceeded message
    quotaExceeded: 'この日付の月間クォータに達しました',
    // Plan subscription badge
    planSubscription: 'プランに含まれる',
    planSubscriptionTooltip: 'サブスクリプションに含まれるイベント',
  },
  // Venues
  venues: {
    title: '会場管理',
    subtitle: 'カラオケ会場を作成・管理',
    venuesList: '会場リスト',
    addVenue: '会場を追加',
    editVenue: '会場を編集',
    noVenues: '登録されている会場がありません',
    noVenuesDescription: '最初の会場を作成することから始めましょう',
    eventName: 'イベント名',
    eventNamePlaceholder: '{{firstName}}のカラオケ',
    name: '会場名',
    namePlaceholder: '例: ル・シャ・ノワールバー',
    address: '住所',
    addressPlaceholder: '例: ミュージック通り123',
    city: '都市',
    cityPlaceholder: '例: 東京',
    country: '国',
    countryPlaceholder: '例: 日本',
    googleMapsUrl: 'Googleマップリンク',
    googleMapsUrlPlaceholder: 'https://maps.google.com/...',
    viewOnGoogleMaps: 'Googleマップで見る',
    addSuccess: '会場が正常に作成されました',
    addError: '会場の作成エラー',
    updateSuccess: '会場が正常に更新されました',
    updateError: '会場の更新エラー',
    deleteSuccess: '会場が正常に削除されました',
    deleteError: '会場の削除エラー',
    confirmDelete: '会場「{{name}}」を削除してもよろしいですか？',
    confirmDeleteTitle: '会場を削除',
    cancel: 'キャンセル',
    add: '追加',
    edit: '編集',
    save: '保存',
    deleting: '削除中...',
    saving: '保存中...',
    generateMapsUrl: 'Google Mapsリンクを生成',
    generateMapsUrlError: 'リンクを生成するには、少なくとも会場名または都市を入力してください',
    mapsModal: {
      title: 'Google Mapsで場所を選択',
      description: 'Google Mapsを使用して、会場の位置を正確に見つけて調整します。',
      instructions: '下の「Google Mapsで開く」をクリックし、必要に応じてマップ上のポイントを調整してから、ブラウザのアドレスバーからリンクをコピーして下のフィールドに貼り付けます。',
      urlLabel: 'Google Mapsリンク',
      openInMaps: 'Google Mapsで開く',
      confirm: '確認',
      clickToOpen: 'クリックしてGoogle Mapsを開く',
      clickDescription: '位置を調整できるように、Google Mapsで新しいウィンドウが開きます',
    },
  },
  // Singer interface
  singer: {
    // Dashboard
    dashboard: 'ダッシュボード',
    dashboardSubtitle: 'あなたのイベントと曲を表示する',
    myEvents: 'マイイベント',
    mySongs: '私の曲',
    allEvents: 'すべて',
    activeEvents: 'アクティブ',
    pastEvents: '過去',
    inactiveEvents: '非アクティブ',
    sortByDate: '日付でソート',
    sortByOrganizer: 'オーガナイザーでソート',
    sortBySongs: '曲数でソート',
    noEvents: 'まだイベントに参加していません。',
    noSongs: 'まだ曲を追加していません。',
    eventName: 'イベント名',
    organizer: 'オーガナイザー',
    date: '日付',
    songsAdded: '追加された曲',
    songsSung: '歌った曲',
    status: 'ステータス',
    status_active: 'アクティブ',
    status_completed: '完了',
    status_cancelled: 'キャンセル',
    status_draft: 'ドラフト',
    title: 'タイトル',
    artist: 'アーティスト',
    event: 'イベント',
    addedDate: '追加日',
    totalEvents: '合計イベント数',
    totalSongs: '合計曲数',
    uniqueOrganizers: 'ユニークなオーガナイザー',
    // Settings
    settings: '設定',
    settingsSubtitle: 'プロフィールとデータを管理する',
    menu: 'シンガーメニュー',
    profile: 'プロフィール',
    changePassword: 'パスワードを変更',
    changePasswordDescription: 'メールでパスワードをリセットするためのリンクを送信します。',
    sendResetLink: 'リセットリンクを送信',
    passwordResetLinkSent: 'パスワードをリセットするためのリンクがメールアドレスに送信されました。',
    accountData: 'アカウントデータ',
    editProfile: 'プロフィールを編集',
    saveChanges: '変更を保存',
    profileUpdated: 'プロフィールが正常に更新されました',
    changePasswordTitle: 'パスワードを変更',
    currentPassword: '現在のパスワード',
    newPassword: '新しいパスワード',
    confirmNewPassword: '新しいパスワードを確認',
    passwordRequirements: 'パスワードは最低12文字で、大文字、小文字、数字、特殊文字を含む必要があります。',
    passwordChanged: 'パスワードが正常に変更されました',
    passwordTooShort: 'パスワードは最低12文字である必要があります',
    passwordsNotMatch: 'パスワードが一致しません',
    currentPasswordRequired: '現在のパスワードは必須です',
    // パスワード検証エラー
    passwordMinLength: 'パスワードは最低12文字である必要があります',
    passwordNoUppercase: 'パスワードには大文字が1つ以上含まれている必要があります',
    passwordNoLowercase: 'パスワードには小文字が1つ以上含まれている必要があります',
    passwordNoDigit: 'パスワードには数字が1つ以上含まれている必要があります',
    passwordNoSpecialChar: 'パスワードには特殊文字が1つ以上含まれている必要があります',
    exportData: 'データをエクスポート',
    exportDataDescription: 'すべての個人データのコピーをJSON形式でダウンロードします。',
    downloadData: 'データをダウンロード',
    dataExported: 'データが正常にエクスポートされました',
    dangerZone: '危険ゾーン',
    deleteAccountDescription: 'アカウントの削除は取り消せません。すべてのデータは永久に削除されます。',
    deleteAccount: 'アカウントを削除',
    deleteAccountConfirm: '削除',
    confirmDeleteAccount: 'アカウントを削除してもよろしいですか？この操作は取り消せません。',
    // QR Code Landing
    qrCodeMissing: 'QRコードトークンがありません',
    qrCodeError: 'QRコードの検証中にエラーが発生しました',
    eventNotFound: 'イベントが見つからないか、トークンが無効です',
    organizerNotAllowed: 'あなたは主催者です。ダッシュボードでこのイベントを管理してください。',
    accessDenied: 'アクセスが拒否されました',
    validatingQRCode: 'QRコードを検証中...',
    chooseAccessMethod: 'イベントへの参加方法を選択',
    continueAsGuest: 'ゲストとして続ける',
    orWithAccount: 'またはアカウントで',
    enterPseudo: 'イベントに参加するためのニックネームを入力',
    joinEvent: 'イベントに参加',
    joining: '接続中...',
    sessionError: 'セッションの作成中にエラーが発生しました',
    joinError: 'イベントへの参加中にエラーが発生しました',
    sessionExpired: 'セッションが期限切れです。イベントのQRコードを再度スキャンしてください。',
    guestMode: 'ゲストモード',
    leaveEvent: 'イベントを退出',
    // Singer Event Page
    eventNotActive: 'このイベントはまだアクティブではありません。後でもう一度お越しください！',
    eventNotStarted: 'このイベントはまだ始まっていません。オーガナイザーがカラオケを開始すると利用可能になります！',
    eventNotYetAvailable: 'このイベントはまだ利用できません。予定された日付までお待ちください。',
    eventEnded: 'このイベントは終了しました。投票は受け付けられなくなりました。',
    eventCancelled: 'このイベントはキャンセルされました。投票は利用できません。',
    searchSong: '曲を検索',
    searchResults: '検索結果',
    advancedSearch: '詳細検索',
    advancedSearchToggle: '詳細検索',
    simpleSearch: 'シンプル検索',
    searchPlaceholder: 'タイトルまたはアーティスト...',
    titlePlaceholder: 'タイトル...',
    artistPlaceholder: 'アーティスト...',
    noResults: '結果が見つかりません',
    addCustomSong: 'このタイトルを追加',
    addCustomSongDescription: 'このタイトルはデータベースに保存され、キューに追加されます。',
    addAndQueue: '追加してキューに入れる',
    localResults: 'データベースで{{count}}件の結果が見つかりました',
    musicbrainzResults: 'MusicBrainzで{{count}}件の結果',
    moreResults: 'さらに結果を表示',
    addError: 'キューへの追加中にエラーが発生しました',
    queueLimitReached: '制限に達しました：このイベントの主催者は、キュー内で最大{{limit}}曲に制限されている無料プランを使用しています。キューには現在{{currentCount}}曲が含まれています。さらに曲を追加したい場合は、主催者にお問い合わせください。',
    songAdded: '「{{title}}」がキューに追加されました！',
    myQueue: '私の曲',
    noSongsInQueue: 'まだ曲を追加していません',
    fullQueue: 'キュー',
    queueEmpty: 'キューは空です',
    you: 'あなた',
    unknown: '不明',
    // Queue statuses
    statusPending: '待機中',
    statusCurrent: '再生中',
    statusCompleted: '完了',
    statusCancelled: 'キャンセル',
  },
  // Subscription and Plans
  subscription: {
    title: 'プランと価格',
    subtitle: 'あなたのニーズに合ったプランを選択してください',
    currentPlan: '現在のプラン',
    planFree: '無料プラン',
    planOneShot: '単一イベント',
    planPro: 'プロプラン',
    planPremium: 'プレミアムプラン',
    planUltimate: 'アルティメットプラン',
    planFreeDescription: '始めるのに最適',
    planOneShotDescription: '1つのイベント向け',
    planProDescription: '定期的な主催者向け',
    planPremiumDescription: '愛好家向け',
    planUltimateDescription: 'プロフェッショナル向け',
    priceFreeFree: '無料',
    priceOneShotPer: '{{price}}€ / イベント',
    priceProPer: '{{price}}€ / 月',
    pricePremiumPer: '{{price}}€ / 月',
    priceUltimatePer: '{{price}}€ / 月',
    maxEvents: '最大イベント数',
    maxSongsPerEvent: 'イベントあたりの曲数',
    unlimited: '無制限',
    features: '機能',
    currentPlanBadge: '現在のプラン',
    changePlan: 'プランを変更',
    upgradePlan: 'プレミアムにアップグレード',
    upgradePlanButton: '上位プランにアップグレード',
    selectPlan: 'このプランを選択',
    monthlyQuota: '月間クォータ',
    freeQuota: '無料クォータ',
    eventsUsed: '{{current}} / {{limit}} イベント',
    billingPeriod: '期間: {{start}} - {{end}}',
    quotaReached: '月間クォータに達しました',
    quotaReachedMessage: 'この期間のイベント制限に達しました。クォータは請求期間の終了時にリセットされます。',
    quotaWarning: '月間クォータがほぼ達しました',
    quotaWarningMessage: 'この期間に残り {{remaining}} イベントがあります。',
    quotaInfo: 'この期間に残り {{remaining}} イベントがあります。',
    learnMore: '詳細を見る',
    contactSupport: 'サポートに連絡',
    freePlanNote: '無料プランは{{maxEvents}}イベント（すべてのステータス、単一イベントを除く）と{{maxSongsPerEvent}}曲/イベントに制限されています。',
    oneShotPlanNote: 'イベントのプレイリストの無制限の曲数。15日間有効。',
    proPlanNote: '{{maxEvents}}イベント/月（ローリング期間）。無制限の曲。',
    premiumPlanNote: '{{maxEvents}}イベント/月（ローリング期間）。無制限の曲。',
    ultimatePlanNote: '無制限のイベントと曲。',
    noEngagement: '契約なし。',
    convertEvent: 'イベントを変換',
    createEvent: 'イベントを作成',
    chooseOption: 'オプションを選択:',
    convertDescription: '無料イベントを選択し、単一イベントプランに変換します。',
    createDescription: '単一イベントプランで新しいイベントを作成します。',
    noFreeEventsToConvert: '変換できる無料イベントがありません。',
    selectFreeEvent: '変換する無料イベントを選択:',
    convertThisEvent: 'このイベントを変換',
    createNewOneShot: '単一イベントを作成',
    newOneShotEventName: '新しい単一イベント',
    createEventError: 'イベントの作成エラー',
    createOneShotEventTitle: '単一イベントを作成',
    proceedToPayment: '支払いに進む',
    // Stripe checkout
    checkoutError: '支払い中にエラーが発生しました',
    paymentSuccessful: '支払いが成功しました!',
    paymentProcessing: 'あなたの支払いは処理されています。まもなくリダイレクトされます。',
    redirectingIn5: '5秒で自動的にリダイレクトされます...',
    backToSubscription: 'プランに戻る',
    successTitle: '支払いが確認されました',
    successSubtitle: 'ご購入ありがとうございます',
    paymentCancelled: '支払いがキャンセルされました',
    checkoutCancelledMessage: '支払いをキャンセルしました。いつでも再度お試しいただけます。',
    cancelledTitle: '支払いがキャンセルされました',
    cancelledSubtitle: 'プランに戻ってもう一度試してください',
    whatHappensNext: '次はどうなりますか?',
    webhooksProcessing: 'お支払いを処理しています',
    subscriptionActive: 'サブスクリプションはまもなくアクティブになります',
    confirmationEmail: '確認メールをお受け取りになります',
    // Subscription cancellation
    cancelConfirm: 'サブスクリプションをキャンセルしてもよろしいですか?',
    cancelDetails: '現在の請求期間の終了まで、プレミアムプランへのアクセスを保持します。その後、無料プランにダウングレードされます。',
    cancelSuccess: 'サブスクリプションがキャンセルされました。',
    cancelPlan: 'キャンセル',
    cancellationInProgress: 'サブスクリプションのキャンセルは既に進行中です。請求期間の終了時に無料プランに切り替わります。',
    cancellationInProgressTitle: 'キャンセル進行中',
    cancellationInProgressMessage: 'サブスクリプションのキャンセルは既に進行中です。現在の請求期間の終了時に無料プランに切り替わります。追加のアクションは必要ありません。',
    accessUntil: '{{date}}までアクセスできます。',
    cancelError: 'サブスクリプションのキャンセル中にエラーが発生しました',
    cancelModalTitle: 'サブスクリプションをキャンセル',
    cancelModalMessage: '現在の請求期間が終了するまで、プレミアムプランへのアクセスは維持されます。その後、無料プランに自動的に切り替わります。',
    cancelModalConfirm: 'はい、サブスクリプションをキャンセルします',
    endOfBillingPeriod: '請求期間の終了',
    // ONE_SHOT upgrade
    upgradeToOneShot: '単一イベントにアップグレード',
    upgradeOneShotConfirm: 'イベント「{{eventName}}」を単一イベントプランにアップグレードしようとしています。',
    oneShotDescription: 'この特定のイベントの無制限の曲のための1回限りの支払いです。',
    price: '価格',
    oneTimePayment: '1回限りの支払い',
    whatYouGet: '得られるもの:',
    unlimitedSongs: 'このイベントの無制限の曲',
    noLimits: 'キューの制限なし',
    oneShotValidity: 'イベント完了まで有効',
    upgradeNow: '今すぐアップグレード',
    upgradeError: 'イベントのアップグレードエラー',
    updateSuccess: 'サブスクリプションが{{plan}}プランに正常に更新されました',
    changePlanTitle: 'プランを変更',
    changePlanSubtitle: 'サブスクリプションを変更しようとしています',
    newPlan: '新しいプラン',
    perMonth: '月',
    upgradeCost: '追加費用: +{{amount}}€ / 月',
    downgradeCredit: '推定クレジット: -{{amount}}€ / 月',
    immediateChange: '即座に変更',
    immediateChangeDescription: 'プランの変更は即座に有効になります。按分計算はStripeによって自動的に計算されます。',
    confirmChange: '変更を確認',
    oneShot: '単発イベント',
    oneShotTooltip: '単発イベント登録',
    expiresLabel: '有効期限',
    oneShotExpirationTitle: '単発イベントの有効期限',
    oneShotDaysRemaining: '残り{{count}}日',
    oneShotDaysRemaining_plural: '残り{{count}}日',
    oneShotExpiresOn: '{{date}}に期限切れ',
    oneShotExpirationWarning: '⚠️ この日付以降、イベントは無料プランに戻ります。期限切れ前に必ずご利用ください！',
    oneShotValidityInfo: 'この購入は購入日から{{days}}日間有効です。期限切れ前にご利用ください！',
    upgradeToUnlimited: '🚀 無制限に切り替え',
    upgradeToUnlimitedNow: '🚀 今すぐ無制限に切り替え',
    upgradeSingleEventConfirm: 'イベント「{{eventName}}」を無制限にアップグレードしようとしています。',
    singleEventDescription: 'この特定のイベントで無制限の曲を利用できる一回限りの支払いです。',
    singleEventPlan: '単発イベント',
    multiplePremiumSuggestion: '複数のイベント？プレミアムプランの方がお得かもしれません。',
    comparePlans: 'プランを比較 →',
    premium: 'プレミアム',
  },
  // Plan alerts
  planAlert: {
    limitReached: 'プラン制限に達しました',
    songsLimitReached: '無料プランの上限({{limit}}曲)に達しました。曲を追加するには、有料プランにサブスクライブしてください。',
    eventsLimitReached: '無料プランの上限({{limit}}イベント)に達しました。イベントを作成するには、有料プランにサブスクライブしてください。',
    upgrade: 'サブスクライブ',
    dismissible: true,
  },
  pwa: {
    install: 'インストール',
    installApp: 'アプリをインストール',
    installPrompt: 'KaraoNowをインストールして素早くアクセス！',
    installed: 'アプリがインストールされました',
    offline: 'オフラインです',
    offlineMessage: '一部の機能が制限される場合があります。',
    updateAvailable: 'アップデートが利用可能',
    updateNow: '今すぐ更新',
  },
  // About
  about: {
    title: 'KaraoNowについて',
    subtitle: 'あなたのカラオケイベントを変えるアプリを発見してください',
    introduction: 'KaraoNowとは？',
    introductionText: 'KaraoNowは、カラオケイベントをシームレスかつプロフェッショナルに管理するために設計されたイノベーティブなSaaSプラットフォームです。イベント主催者であろうと情熱的なシンガーであろうと、KaraoNowはあなたに忘れられないカラオケ体験を作成するために必要なツールを提供します。',
    missionText: '私たちのミッションは、カラオケプレイリストの管理を簡素化し、主催者とシンガーの間に本物のつながりを作成し、各イベントをとても記念すべきでよく組織されたものにすることです。',
    features: '主な機能',
    feature1Title: 'イベント管理',
    feature1Desc: '必要なすべての詳細を含むカラオケイベントを簡単に作成・管理します。',
    feature2Title: 'インタラクティブな参加',
    feature2Desc: 'シンガーがQRコード経由でイベントに参加し、お気に入りの曲に投票できるようにします。',
    feature3Title: 'リアルタイムプレイリスト',
    feature3Desc: 'ライブで曲キューを表示・管理し、インスタントアップデートを実現します。',
    feature4Title: '詳細な統計',
    feature4Desc: 'チャートとリアルタイムメトリクスを使用してイベントパフォーマンスを分析します。',
    feature5Title: '強化されたセキュリティ',
    feature5Desc: 'あなたのデータは高いセキュリティ基準と堅牢なクラウドインフラストラクチャで保護されています。',
    feature6Title: 'マルチ言語サポート',
    feature6Desc: 'KaraoNowは8言語をサポートし、グローバルオーディエンスに到達します。',
    technology: '技術スタック',
    frontend: 'フロントエンド',
    backend: 'バックエンド',
    version: '現在のバージョン',
    versionNote: '定期的に新機能で更新されます',
    visitWebsite: '公式ウェブサイト',
    support: 'サポート',
    supportNote: 'ご質問はお気軽にお問い合わせください',
    copyright: 'すべての権利が予約されています。',
    privacyNotice: 'データ保護の詳細については、法的ページをご確認ください。',
    tryForFree: '無料トライアルを開始',
  },
  // Organizer
  organizer: {
    // Dashboard
    dashboard: 'ダッシュボード',
    dashboardSubtitle: 'あなたのカラオケイベントを管理する',
    menu: 'オーガナイザーメニュー',
    // Settings
    settings: '設定',
    settingsSubtitle: 'プロフィールと設定を管理する',
    editProfile: 'プロフィールを編集する',
    saveChanges: '変更を保存する',
    profileUpdated: 'プロフィールが正常に更新されました',
    emailChangeConfirmTitle: 'メールアドレスの変更',
    emailChangeConfirmMessage: '確認メールが新しいメールアドレスに送信されます。新しいメールアドレスは、そのメール内のリンクを確認した後にのみ有効になります。古いメールアドレスは確認まで有効です。',
    changePassword: 'パスワードを変更する',
    changePasswordDescription: 'メールでパスワードをリセットするためのリンクを送信します。',
    sendResetLink: 'リセットリンクを送信する',
    passwordResetLinkSent: 'パスワードリセットリンクがメールに送信されました。',
    accountData: 'アカウントデータ',
    exportData: 'データをエクスポートする',
    exportDataDescription: 'すべての個人データのコピーをJSON形式でダウンロードしてください。',
    downloadData: 'データをダウンロードする',
    dataExported: 'データが正常にエクスポートされました',
    dangerZone: '危険ゾーン',
    deleteAccountDescription: 'アカウントの削除は取り消せません。すべてのデータが永久に削除されます。',
    deleteAccount: 'アカウントを削除する',
    deleteAccountConfirm: '削除',
    confirmDeleteAccount: 'アカウントを削除してもよろしいですか?このアクションは取り消せません。',
    // Plan section
    planSection: 'サブスクリプション',
    planSectionDescription: '現在のプラン、制限、アップグレードオプションを表示する',
    renewDate: '更新日: ',
    viewPlanDetails: 'プラン詳細を表示',
    eventsLimit: '作成されたイベント（ユニークイベントを除く）',
    subscriptionStatus: 'ステータス',
    statusActive: 'アクティブ',
    statusInactive: '非アクティブ',
    statusScheduledEnd: '終了予定',
    subscriptionExpires: '有効期限',
    subscriptionRenewsOn: '更新日',
    cancellationConfirmed: 'キャンセル確認済み！',
    continueAccessUntil: '{{date}}までプレミアムプランをご利用いただけます',
    autoDowngradeToFree: 'この日以降、無料プランに自動的に切り替わります。',
    accessUntilEndOfPeriod: '現在の請求期間が終了するまでアクセス可能です。',
    planFeatures: 'プラン機能',
    featureEvents: '月別イベント',
    featureSongsPerEvent: 'イベント当たりの曲',
    unlimited: '無制限',
    upgradePlan: 'プランをアップグレードする',
    managePlan: 'サブスクリプションを管理する',
  },
  // Email change
  email: {
    changeEmail: 'メールアドレスを変更する',
    changeEmailDescription: 'メールアドレスを変更するには、まず新しいアドレスを確認する必要があります。確認メールが新しいアドレスに送信されます。',
    currentEmail: '現在のメール',
    newEmail: '新しいメールアドレス',
    newEmailAddress: '新しいアドレス',
    requestEmailChange: 'メール変更をリクエストする',
    emailRequired: 'メールアドレスは必須です',
    emailUnchanged: '新しいメールアドレスは現在のメールアドレスと異なる必要があります',
    emailChangeConfirmationSent: '確認メールが新しいメールアドレスに送信されました。',
    emailChangeInfo: '新しいアドレスが検証されるまで、古いメールはアクティブなままです。',
    verifying: '確認中',
    verifyingDescription: 'メールアドレスを確認中...',
    success: 'メール確認済み',
    confirmationSuccess: 'メールアドレスが正常に変更されました!',
    confirmationFailed: '確認に失敗しました',
    linkExpired: 'リンクの有効期限が切れています',
    tokenExpired: '確認リンクの有効期限が切れています。新しいリンクをリクエストしてください。',
    linkExpiredDescription: '確認リンクの有効期限が切れています(24時間有効)。新しい確認リンクをリクエストしてください。',
    redirecting: 'リダイレクト中...',
    redirectingToLogin: '3秒でログインにリダイレクトします...',
    goToSettings: '設定に移動',
    backToSettings: '設定に戻る',
    requestNewLink: '新しいリンクをリクエストする',
    missingToken: '確認トークンがありません。',
    validationPending: '検証保留中',
    pendingEmailAddress: '検証待ち中',
  },
  // RGPD - データプライバシー
  rgpd: {
    pageTitle: '個人データ',
    pageSubtitle: 'あなたのデータとアカウントを安全に管理してください',
    exportData: 'データをダウンロード',
    exportDataDescription: '個人データの完全なコピーをJSON形式でダウンロードします。このデータを他の場所にインポートするか、バックアップとして保存できます。',
    exportIncludes: 'このエクスポートに含まれるもの',
    exportProfile: 'プロファイルと設定',
    exportHistory: 'イベント履歴',
    exportEvents: '作成されたイベント',
    exportSubscription: 'サブスクリプション情報',
    downloadData: 'ダウンロード（JSON）',
    exporting: 'ダウンロード中...',
    exportFormatJSON: 'フォーマット：JSON（GDPR準拠）',
    dataExportedSuccess: '✅ データが正常にダウンロードされました！',
    deleteAccount: 'アカウント削除',
    deleteAccountDescription: 'このアクションはあなたのアカウントと関連するすべてのデータを永久に削除します。この操作は取り消すことができません。',
    deleteWarning: '⚠️ 重要な警告',
    deleteIrreversible: 'このアクションは永続的で取り消せません',
    deleteAllData: 'すべてのデータが削除されます',
    deleteNoRecovery: '復旧の可能性はありません',
    deleteRecommendation: 'アカウントを削除する前にデータをダウンロードすることをお勧めします。',
    deleteAccountButton: 'アカウント削除',
    confirmDeletion: '削除を確認',
    deleteConfirmMessage: 'アカウントを削除してもよろしいですか？この操作は取り消すことができません。',
    continueDelete: '削除に進む',
    finalDeleteWarning: '最終確認 - 戻れません',
    finalDeleteMessage: 'これが最後のチャンスです！「永久に削除」をクリックするとアカウントが取り消せないように削除されます。',
    permanentlyDelete: '永久に削除',
    gdprCompliance: 'GDPR準拠',
    gdprInfo: 'GDPR規制に従い、個人データへのアクセス、修正、削除、およびポート化の権利があります。これらのツールを使用してこれらの権利を行使できます。',
  },
  // Cookies consent
  cookies: {
    title: '🍪 クッキーを使用します',
    description: 'クッキーを使用してエクスペリエンスを向上させます。詳細については、ポリシーを確認してください。',
    understood: 'わかりました',
    learnMore: '詳細',
    footerNote: 'クッキーポリシーで詳細を確認できます。',
  },
  // Cookies policy page
  cookiesPage: {
    title: 'クッキーポリシー',
    subtitle: 'KaraoNowで使用されるクッキーについての完全な情報',
    overview: '概要',
    overviewText: 'KaraoNowはクッキーを使用して、ユーザーエクスペリエンスを向上させ、アカウントを保護し、プラットフォームの使用方法を理解します。このポリシーでは、クッキーをどのように、なぜ使用するかを説明しています。',
    whatAreCookies: 'クッキーとは何ですか？',
    whatAreCookiesText: 'クッキーはデバイスに保存される小さなテキストファイルです。これにより、ウェブサイトはあなたの訪問に関する情報を記憶できます。',
    typesOfCookies: 'クッキーの種類',
    sessionCookies: 'セッションクッキー',
    sessionCookiesText: 'これらのクッキーはブラウザを閉じると期限切れになります。セッションをアクティブに保ち、アクセスを保護するために使用されます。',
    persistentCookies: '永続クッキー',
    persistentCookiesText: 'これらのクッキーは定義された期間、デバイスに保存されたままです。次回の訪問時にあなたを認識することができます。',
    howWeUseCookies: 'クッキーの使用方法',
    useAuthentication: '認証とセッションセキュリティ',
    usePreferences: 'あなたの設定を記憶 (言語、テーマ)',
    useSecurity: '不正使用と攻撃からの保護',
    usePerformance: 'プラットフォームのパフォーマンス改善',
    yourRights: 'あなたの権利',
    yourRightsText: 'クッキーをコントロールする権利があります。同意バナーを通じて、必須ではないクッキーを受け入れたり拒否したりできます。',
    managingCookies: 'クッキーの管理',
    managingCookiesText: 'ブラウザの設定でクッキーを管理できます。特定のクッキーを無効にするとKaraoNowの機能に影響する可能性があることに注意してください。',
    contact: 'お問い合わせ',
    contactText: 'このポリシーについてご不明な点がございましたら、お問い合わせページまたはサービス利用規約からお問い合わせください。',
    footer: 'ご質問がありますか？ホームに戻ります。',
  },
  // Singer menu
  singerMenu: {
    about: 'について',
    dataPrivacy: '個人データ',
  },
  // Footer
  footer: {
    legal: '法務情報',
    privacy: 'プライバシーポリシー',
    cookies: 'クッキーポリシー',
    terms: '利用規約',
  },
  // 法的通知ページ
  legalPage: {
    title: '法的通知',
    subtitle: 'KaraoNowに関する法的情報',
    editor: {
      title: 'サイト編集者',
      name: 'axialdata',
      address: 'Impasse du pélican, 97233 Schoelcher, マルティニーク',
      phone: '電話: +596696367741',
      email: 'メール: contact@axialdata.net',
      siret: 'SIRET: 49347625300023',
    },
    publication: {
      title: '出版責任者',
      director: 'Pierre-Yves Léglise',
    },
    hosting: {
      title: 'ホスティング',
    },
    dataProtection: {
      title: 'データ保護',
      description: 'お客様の個人データの保護の詳細については、当社のプライバシーポリシーをご覧ください。',
      link: 'プライバシーポリシーを読む',
    },
  },
  // プライバシーポリシーページ
  privacyPage: {
    title: 'プライバシーポリシー',
    subtitle: '個人データの保護方法',
    lastUpdated: '最終更新: 2025年1月1日',
    intro: {
      title: 'はじめに',
      description: 'KaraoNowはお客様のプライバシー保護に努めています。このポリシーでは、GDPRに準拠したお客様の個人データの収集、使用、保護方法について説明します。',
    },
    dataCollection: {
      title: '収集されるデータ',
      organizers: {
        title: '主催者向け',
        email: 'メールアドレス',
        name: '名前、姓、組織',
        organization: '組織の詳細',
        payment: '支払い情報（サードパーティプロセッサを介して）',
        events: '作成されたイベントに関する情報',
      },
      singers: {
        title: '歌手向け',
        email: 'メールアドレス（オプション）',
        pseudo: 'ユーザーペンネーム',
        name: '名前と姓（オプション）',
        participation: '参加したイベント',
        history: 'キューに追加された曲の履歴',
      },
    },
    purposes: {
      title: 'データの使用目的',
      authentication: '認証とアカウント管理',
      eventManagement: 'イベント作成と管理',
      songManagement: '曲キューの管理',
      communication: 'アカウントに関する重要な通信',
      billing: '支払い処理と請求',
      support: '顧客サポートと技術サポート',
      preferences: 'ユーザー体験の向上',
    },
    legalBasis: {
      title: '処理の法的根拠',
      description: 'お客様のデータ処理は、登録時のご同意とKaraoNowとの間のサービス契約の実行に基づいています。',
    },
    retention: {
      title: 'データ保持期間',
      active: 'アクティブなデータ: アカウントがアクティブである限り保持',
      inactive: '非アクティブデータ: 最後のログインから12か月後に削除',
      legal: '法的データ: 法律で要求される限り保持',
    },
    rights: {
      title: 'GDPR権',
      description: 'GDPRの下で、以下の権利があります：',
      access: '個人データへのアクセス権',
      rectification: 'データの修正権',
      deletion: '忘れられる権利（データ削除）',
      portability: 'データ移行権',
      opposition: '処理への異議を唱える権利',
      limitation: '処理を制限する権利',
      contact: 'これらの権利を行使するには、support@karaonow.comでお問い合わせください',
    },
    security: {
      title: 'セキュリティ',
      description: 'お客様のデータを不正アクセス、変更、または開示から保護するための適切なセキュリティ対策を使用しています。',
    },
    cookies: {
      title: 'クッキー',
      description: 'KaraoNowはクッキーを使用してユーザー体験を向上させています。詳細については、クッキーポリシーをご覧ください。',
      link: 'クッキーポリシーを読む',
    },
    contact: {
      title: 'お問い合わせ',
      description: 'このポリシーまたは個人データに関するご質問がある場合はお問い合わせください：',
      email: 'support@karaonow.com',
    },
  },
  // 利用規約ページ
  termsPage: {
    title: '利用規約',
    subtitle: 'KaraoNow利用規約',
    lastUpdated: '最終更新: 2025年1月1日',
    intro: {
      title: 'はじめに',
      description: 'KaraoNowへようこそ！当社のプラットフォームを使用することにより、お客様はこれらの利用規約に同意します。これらを慎重にお読みください。',
    },
    service: {
      title: 'サービスの説明',
      description: 'KaraoNowはカラオケイベント管理用のSaaSプラットフォームです。プラットフォームは2つのメインインターフェースを提供します：',
      organizers: {
        title: '主催者向け',
        description: '主催者はイベントを作成し、歌手と共有するQRコードを生成し、曲キューを管理し、歌手の順番を変更できます。サブスクリプションシステムは3つのプランを提供します：無料（制限付き）、シングルイベント、プレミアム（月間サブスクリプション）。',
      },
      singers: {
        title: '歌手向け',
        description: '歌手はQRコードを介してイベントに参加し、イベントキューに曲を追加し、曲の順序を確認できます。歌手はアカウントなし（ゲストモード）で参加することも、高度な機能のためにアカウントを使用することもできます。',
      },
    },
    accounts: {
      title: 'ユーザーアカウント',
      description: 'KaraoNowを使用するには、アカウントを作成する必要があります。認証情報の機密性を保つ責任はお客様にあります。',
      responsibility: 'お客様はアカウント経由で実行されたすべてのアクティビティについて責任があります',
      accuracy: 'お客様は正確かつ最新の情報を提供することに同意します',
      security: 'お客様はパスワードのセキュリティを保つことに同意します',
      notTransferable: 'アカウントは個人的なもので、譲渡不可能です',
    },
    subscription: {
      title: 'サブスクリプションプラン',
      description: 'KaraoNowは主催者向けに複数のサブスクリプションプランを提供します：',
      free: 'フリープラン：最大4つのイベント（すべてのステータスを組み合わせ、ユニークイベントを除く）、イベントあたり15曲',
      oneShot: 'ユニークイベント：特定のイベントに対する一回限りの支払い、無制限の曲、有効期限が制限されています',
      premium: '月額サブスクリプション付きの有料プラン：月間イベントクォータ付きの複数のプラン、イベントあたり無制限の曲、自動月間更新',
    },
    payment: {
      title: '支払い条件',
      description: '支払いは安全なサードパーティの支払いプロセッサを通じて処理されます。',
      currency: '支払いはユーロ（EUR）で行われます',
      noRefund: 'すでに提供されたサービスに対して払戻は行われません',
      cancellation: 'サブスクリプションはいつでもキャンセルできます',
      automaticRenewal: '月額サブスクリプションはキャンセルされない限り毎月自動的に更新されます',
    },
    conduct: {
      title: 'ユーザーの行動',
      description: 'お客様はKaraoNowを合法的かつ倫理的に使用することに同意します。',
      prohibited: {
        title: '禁止事項',
        illegal: '違法な活動または適用法の違反',
        harmful: '有害、不快感を与える、または名誉毀損的なコンテンツ',
        harassing: 'ハラスメント、脅迫、または差別',
        spam: 'スパムまたは不要な通信の送信',
        fraud: '詐欺行為またはスキマの試み',
      },
    },
    organizerResponsibilities: {
      title: '主催者の責任',
      description: '主催者は以下の責任を受け入れます：',
      songSelection: '曲の内容の合法性についてお客様が責任を負います',
      eventManagement: 'お客様はこれらの条件に従ってイベントを管理する必要があります',
      dataManagement: 'お客様は歌手のデータ管理に責任があります',
      copyrightCompliance: 'お客様は曲の著作権法の遵守に同意します',
    },
    singerRights: {
      title: '歌手の権利と責任',
      description: '歌手は以下の権利を有し、以下の責任を受け入れます：',
      participation: '他の主催者が開催するイベントに参加できます',
      songSelection: 'イベントキューに曲を追加できます',
      noDuplication: '同じ曲を複数回追加することはできません',
      respect: '主催者が設定したルールを尊重することに同意します',
    },
    intellectualProperty: {
      title: '知的財産権',
      description: 'KaraoNowおよびそのコンテンツ（音楽曲を除く）のすべての知的財産権はKaraoNowに属します。許可なくコンテンツを複製または使用することはできません。',
    },
    liability: {
      title: '責任の制限',
      description: 'KaraoNowは「現状のまま」提供されています。当社はプラットフォーム使用から生じる直接的または間接的な損害について責任を負いません。当社の責任は先月のサブスクリプション料金の最大額に制限されます。',
    },
    termination: {
      title: '終了',
      description: 'KaraoNowはこれらの条件違反の場合、アカウントを停止または終了する権利を留保します。いつでもアカウントを終了できます。',
    },
    modifications: {
      title: '条件の変更',
      description: 'KaraoNowはこれらの条件をいつでも変更する権利を留保します。変更は通知から30日後に有効になります。その後のプラットフォームの継続的な使用は、新しい条件への同意を意味します。',
    },
    jurisdiction: {
      title: '管轄権',
      description: 'これらの条件はフランス法に準拠します。すべての紛争はフランスの管轄裁判所に提出されます。',
    },
    contact: {
      title: 'お問い合わせ',
      description: 'これらの条件に関するご質問があればお問い合わせください：',
      email: 'support@karaonow.com',
    },
  },
}
